import type { CaptureContext, MonitoringProvider } from "../types";

// Sentry provider.
// - Browser: uses the official @sentry/react SDK (initialized in
//   src/lib/monitoring/sentry-client.ts on app startup in production).
// - Server (Cloudflare Worker / Node): posts directly to the Sentry
//   envelope endpoint via fetch — avoids Node-only Sentry SDK internals.
export const sentryMonitoringProvider: MonitoringProvider = {
  name: "sentry",
  async capture(error, context) {
    // Browser path — delegate to the SDK when available.
    if (typeof window !== "undefined") {
      try {
        const Sentry = await import("@sentry/react");
        const eventId = Sentry.captureException(error, {
          level: context?.severity ?? "error",
          tags: {
            source: context?.source ?? "unknown",
            ...(context?.location ? { location: context.location } : {}),
            ...(context?.tags ?? {}),
          },
          extra: context?.extra,
          user: context?.userId ? { id: context.userId } : undefined,
        });
        return eventId;
      } catch (err) {
        const msg = err instanceof Error ? err.message : "sentry_sdk_error";
        console.error(`[monitor:sentry] browser sdk failed: ${msg}`);
        // fall through to envelope fetch below
      }
    }

    const dsn = readDsn();
    if (!dsn) {
      console.error("[monitor:sentry] dsn_missing");
      return undefined;
    }
    try {
      const parsed = parseDsn(dsn);
      if (!parsed) throw new Error("sentry_dsn_invalid");
      const eventId = randomHex(32);
      const timestamp = new Date().toISOString();
      const err = normalizeError(error);
      const envelope = buildEnvelope(eventId, timestamp, dsn, err, context);
      const res = await fetch(parsed.envelopeUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-sentry-envelope" },
        body: envelope,
        keepalive: true,
      });
      if (!res.ok) {
        // Read Sentry's response body so callers can see WHY it was rejected.
        let body = "";
        try {
          body = (await res.text()).slice(0, 500);
        } catch {
          /* ignore */
        }
        const detail = body ? `: ${body.replace(/\s+/g, " ")}` : "";
        console.error(`[monitor:sentry] ingest_status_${res.status}${detail}`);
        throw new Error(`sentry_ingest_status_${res.status}${detail}`);
      }
      return eventId;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "sentry_unknown_error";
      console.error(`[monitor:sentry] ${msg}`);
      throw err instanceof Error ? err : new Error(msg);
    }
  },
};

function readEnv(name: string): string | undefined {
  if (typeof process !== "undefined" && process.env?.[name]) return process.env[name];
  if (typeof import.meta !== "undefined") {
    const v = (import.meta as { env?: Record<string, string | undefined> }).env?.[`VITE_${name}`];
    if (v) return v;
  }
  return undefined;
}

function readDsn(): string | undefined {
  if (typeof process !== "undefined" && process.env?.SENTRY_DSN) return process.env.SENTRY_DSN;
  if (typeof import.meta !== "undefined") {
    const v = (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_SENTRY_DSN;
    if (v) return v;
  }
  return undefined;
}

function parseDsn(dsn: string) {
  try {
    const u = new URL(dsn);
    const publicKey = u.username;
    const projectId = u.pathname.replace(/^\//, "");
    if (!publicKey || !projectId) return null;
    const envelopeUrl = `${u.protocol}//${u.host}/api/${projectId}/envelope/?sentry_key=${publicKey}&sentry_version=7`;
    return { publicKey, projectId, envelopeUrl };
  } catch {
    return null;
  }
}

function normalizeError(error: unknown) {
  if (error instanceof Error) {
    return {
      type: error.name,
      value: error.message,
      stacktrace: error.stack ? { frames: parseStack(error.stack) } : undefined,
    };
  }
  return { type: "Error", value: String(error) };
}

function parseStack(stack: string) {
  return stack
    .split("\n")
    .slice(1, 30)
    .map((line) => ({ filename: line.trim() }));
}

function buildEnvelope(
  eventId: string,
  timestamp: string,
  dsn: string,
  err: ReturnType<typeof normalizeError>,
  context?: CaptureContext,
) {
  // Envelope header — `dsn` MUST be the full DSN, not `sentry_key=...`.
  // https://develop.sentry.dev/sdk/envelopes/#envelope-headers
  const header = JSON.stringify({
    event_id: eventId,
    sent_at: timestamp,
    dsn,
  });
  const environment = readEnv("SENTRY_ENVIRONMENT") ?? readEnv("NODE_ENV") ?? "production";
  const release = readEnv("SENTRY_RELEASE") ?? readEnv("APP_VERSION");

  // Tag values must be strings (max 200 chars).
  const rawTags: Record<string, unknown> = {
    source: context?.source ?? "unknown",
    ...(context?.location ? { location: context.location } : {}),
    ...(context?.tags ?? {}),
  };
  const tags: Record<string, string> = {};
  for (const [k, v] of Object.entries(rawTags)) {
    if (v === undefined || v === null) continue;
    tags[k] = String(v).slice(0, 200);
  }

  const payload = JSON.stringify({
    event_id: eventId,
    timestamp,
    level: context?.severity ?? "error",
    platform: "javascript",
    environment,
    ...(release ? { release } : {}),
    tags,
    extra: context?.extra,
    user: context?.userId ? { id: context.userId } : undefined,
    exception: { values: [err] },
  });
  const itemHeader = JSON.stringify({
    type: "event",
    content_type: "application/json",
    length: new TextEncoder().encode(payload).length,
  });
  return `${header}\n${itemHeader}\n${payload}`;
}

function randomHex(len: number) {
  const bytes = new Uint8Array(len / 2);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}
